import { FC } from 'react'
import { generateSrcSet } from 'project/image-proxy-web'
import useWindowSize from 'components/util/useWindowSize'
import { ReactNode } from 'preact/compat'
import classNames from 'classnames'
import { sectionPadding, sectionPaddingLarge } from 'project/padding'
import Heading from 'components/Heading'

export const saasSolutionsHighlights = [
  {
    content:
      'Our virtual office product with HaiRo™ mail management and processing comes with a simple enterprise-level commercial model. You can pass on your costs and apply a markup to create a recurring revenue stream. Our virtual office solution also improves customer lifetime value and customer satisfaction by offering the most advanced solution on the market. Registered business services for your clients as it should be.',
    title: 'Recurring revenue streams & improved customer value.',
  },
  {
    content:
      'We have worked with 100+ partners to support 30,000+ of their clients in the finance, banking, digital and accounting sectors to offer our virtual business services direct to their clients. Our SaaS solution is available as plug-and-play for smaller clients, through full end-to-end white label integrations. All at no upfront cost to you.',
    title:
      'Offer our products and services to your clients with NO upfront cost.',
  },
  {
    content:
      'We have integrated our virtual office services into most business scenarios.  From simple plug-and-play models to fully white labelled integrations.  Our expertise in this sector and our technical background give us an unmatched advantage.  Our experience with our partners speaks for itself.',
    title: 'Enterprise solutions for virtual office services.',
  },
]

export const virtualOfficeHighlights = [
  {
    content:
      'HaiRo™ scans, sorts, and delivers your mail digitally, letting you see who it’s from at a glance.',
    title: 'AI-Powered Mail Management',
  },
  {
    content:
      'Add appointments and deadlines to your calendar, share important documents with colleagues, and organise correspondence effortlessly.',
    title: 'Turn Mail into Smart Actions',
  },
  {
    content:
      'Every scanned document is searchable, stored safely, and accessible 24/7.',
    title: 'Stay Organised & Secure',
  },
  {
    content:
      'Update AML details, payment settings, and invoices all in one place.',
    title: 'Manage Business Essentials',
  },
]

export const smallBusinessHighlights = [
  {
    content:
      "Offering a registered office address, or director service address is just the start for a client. It creates a simple way to separate business and work, allows formal correspondence to be sent to the company's registered office address and keeps your home address separate and private. Having a registered office address and a registered service address is also a legal obligation at Companies House. In fact, using your home address as your registered office address or service address is not really recommended - as it is then publicly available.",
    title: '',
  },
  {
    content:
      'Given all of that, it makes sense to offer your client a registered office address service. With that come the responsibilities around physical mail and mail handling, and providing an additional service. Most of our partners who offer registered office address services to their company address quickly encounter these issues. The key areas of friction around registered office address services come from physical mail handling, sorting and forwarding, customer support for the service, and the additional client frameworks and volumes of scale.',
    title: 'Taking your clients needs to the next level.',
  },
  {
    content:
      'When your client signs up to a registered office address service with us, we immediately remove all friction and add in a massive element of efficiency. As soon as they use our address for their company\'s registered office address, and apply it to their marketing materials, their mail will automatically get delivered to our prime central London location. And once in our care, it gets scanned, digitised and made available to the client within 1 working day. All from a simple registered office address.',
    title: 'Our registered office address service removes these frictions. ',
  },
  {
    content: `A registered service address is an official address, registered with Companies House, where all directors, founders and persons with significant control are required to have one listed.
<br/>
<br/>
A registered office address is the official address of a company that is registered with Companies House. All correspondence and legal notices relating to the company are sent to this address.
<br/>
<br/>
The difference between a registered office address and a registered service address — both need to be registered at Companies House — is that the registered service address can be anywhere in the world.`,
    title:
      'The difference between a registered office address and a registered service address.',
  },
  {
    content: `How do we process over a million pieces of mail with speed and precision? By combining enterprise-grade scanning with custom-built automation, we’ve transformed the way mail is handled—eliminating manual steps and ensuring accuracy at scale.
<br/>
<br/>
Our intelligent system, powered by machine learning, doesn’t just scan mail—it understands it. From sorting and categorising to fraud detection and compliance checks, every piece of mail is processed in real-time, seamlessly integrated with IBML Fusion scanners for a fully optimised workflow.
<br/>
<br/>
By rethinking traditional mail processing, we’ve built an industry-leading system that delivers efficiency, security, and automation - helping businesses manage their post with zero friction.`,
    title: 'The Postroom, Reinvented.',
  },
  {
    content: `We have helped over 60,000 SMEs register their company's registered office address and / or registered service address. We have worked with over 100+ Partners in the tech and finance space to create a registered office and service address product to sell to their clients.`,
    title: 'We’ve already helped thousands of companies.',
  },
  {
    content: `Our product is simple: we offer a single plan that includes a registered office address / service address, all mail handling and processing and our proprietary document management system.
<br/>
<br/>
Our integration approach is simple: our registered office address platform can be used as plug-and-play or is available via a fully integrated white label API.
<br/>
<br/>
You offer this service to your clients and we take care of the rest. We have a simple step-by-step guide for clients to register their company's registered office and service address with Companies House`,
    title:
      'How to get started with our Registered Office Address as a Service (RAaaS).',
  },
]

type Props = {
  title: string
  subtitle?: ReactNode
  children: ReactNode
  headingSize?: 1 | 2 | 3 | 4 | 5 | 6
  hideImage?: boolean
}

const MailPortalHighlights: FC<Props> = ({
  children,
  headingSize = 2,
  hideImage,
  subtitle,
  title,
}) => {
  const size = useWindowSize()
  return (
    <div className='d-flex flex-column align-items-center'>
      <div
        className={classNames(
          'd-flex flex-column lh-114 text-center fw-semibold gap-2 gap-lg-3 mb-4',
          sectionPadding,
          {
            'mb-lg-7': subtitle,
            'mb-lg-9': !subtitle,
          },
        )}
        style={{
          maxWidth: 1010,
        }}
      >
        <Heading
          size={headingSize}
          className='fs-fluid-36-80 lh-105 fw-semibold mb-0'
        >
          {title}
        </Heading>
        {subtitle}
      </div>
      <div className={classNames('d-flex flex-column', sectionPaddingLarge)}>
        {!hideImage && (
          <img
            {...generateSrcSet(
              size.isLgOrHigher
                ? '/images/new/customer-portal-screenshots.png'
                : '/images/new/customer-portal-screenshots-mobile.png',
              size.isLgOrHigher ? 725 : 393,
              size.isLgOrHigher ? 421 : 740,
            )}
            loading='lazy'
            height={size.isLgOrHigher ? 421 : 740}
            width={size.isLgOrHigher ? 725 : 393}
            alt='MailPortal Screenshots'
            className='img-fluid my-4 mx-auto'
          />
        )}
        <div className={classNames('fs-fluid-18-24 lh-140 mt-14 mt-lg-8')}>
          {children}
        </div>
      </div>
    </div>
  )
}

export default MailPortalHighlights
